import { Text } from "@solomon-labs/design/components/ui";
import { useState } from "react";
import type { FormEvent, ReactElement } from "react";
import { Link } from "react-router-dom";

import {
    OrgAuthRateLimitError,
    requestOrgPasswordReset
} from "../../services/orgAuthService";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Spinner } from "../ui/spinner";

import { validateOrgAuthForgotPasswordEmail } from "./orgAuthLoginValidation";
import { AUTH_INPUT_CLASS, OrgAuthField, OrgAuthShell } from "./OrgAuthShell";

const EMAIL_ERROR_ID = "org-auth-forgot-email-error";
const FORM_ERROR_ID = "org-auth-forgot-form-error";

const GENERIC_ERROR =
    "Something went wrong sending the reset email. Try again in a moment.";

interface OrgAuthForgotPasswordPageProps {
    apiBase: string;
    footerNote?: string;
}

const INLINE_ACTION_CLASS =
    "underline underline-offset-2 hover:text-foreground-muted disabled:opacity-50";

/**
 * Self-serve "forgot password" page shared by both consoles. Submitting
 * always lands on the same confirmation view whether or not the email has
 * an account (the backend is anti-enumeration; the reset link only ever
 * arrives by email).
 */
export function OrgAuthForgotPasswordPage({
    apiBase,
    footerNote
}: OrgAuthForgotPasswordPageProps): ReactElement {
    const [email, setEmail] = useState("");
    const [sentTo, setSentTo] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);

    const submit = async (targetEmail: string): Promise<void> => {
        setIsSubmitting(true);
        setFormError(null);
        try {
            await requestOrgPasswordReset(apiBase, targetEmail);
            setSentTo(targetEmail);
        } catch (caught) {
            // Rate-limit copy names the wait; anything else stays generic.
            setFormError(
                caught instanceof OrgAuthRateLimitError
                    ? caught.message
                    : GENERIC_ERROR
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = (e: FormEvent): void => {
        e.preventDefault();
        if (isSubmitting) return;

        const { emailError: nextError, normalizedEmail } =
            validateOrgAuthForgotPasswordEmail(email);
        setEmailError(nextError);
        if (nextError) return;

        setEmail(normalizedEmail);
        void submit(normalizedEmail);
    };

    const handleResend = (): void => {
        if (isSubmitting || !sentTo) return;
        void submit(sentTo);
    };

    const handleEditEmail = (): void => {
        // "Reconfirm your email address": back to the form with the address
        // still filled in so a typo is easy to fix.
        setSentTo(null);
        setFormError(null);
    };

    if (sentTo) {
        return (
            <OrgAuthShell title="Check your email" footerNote={footerNote}>
                <div className="flex flex-col gap-6">
                    <div className="flex flex-col gap-3">
                        <Text
                            variant="small"
                            className="text-foreground-base text-pretty"
                        >
                            If {sentTo} matches an email address we have on
                            file, we&apos;ve sent you a link to reset your
                            password.
                        </Text>
                        <Text
                            variant="small"
                            className="text-foreground-muted text-pretty"
                        >
                            If you haven&apos;t received the email in 5 minutes,
                            check your spam folder,{" "}
                            <button
                                type="button"
                                onClick={handleResend}
                                disabled={isSubmitting}
                                className={INLINE_ACTION_CLASS}
                            >
                                resend
                            </button>
                            , or{" "}
                            <button
                                type="button"
                                onClick={handleEditEmail}
                                disabled={isSubmitting}
                                className={INLINE_ACTION_CLASS}
                            >
                                reconfirm your email address
                            </button>
                            .
                        </Text>
                        {formError && (
                            <Text
                                id={FORM_ERROR_ID}
                                variant="small"
                                role="alert"
                                aria-live="polite"
                                className="text-error-base text-pretty"
                            >
                                {formError}
                            </Text>
                        )}
                    </div>

                    <Button
                        variant="raised"
                        interaction="lifted"
                        size="md"
                        fullWidth
                        disabled={isSubmitting}
                        render={<Link to="/login" />}
                    >
                        {isSubmitting ? (
                            <span className="flex items-center justify-center gap-2">
                                <Spinner surface="dark" className="size-3.5" />
                                Sending
                            </span>
                        ) : (
                            "Return to sign-in"
                        )}
                    </Button>
                </div>
            </OrgAuthShell>
        );
    }

    return (
        <OrgAuthShell
            title="Forgot your password?"
            intro="Enter your email address and we'll send you a link to reset your password."
            footerNote={footerNote}
        >
            <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-6"
                noValidate
            >
                <div className="flex flex-col gap-4">
                    <OrgAuthField id="forgot-email" label="Email">
                        <Input
                            id="forgot-email"
                            type="email"
                            autoComplete="email"
                            inputMode="email"
                            autoCapitalize="none"
                            spellCheck={false}
                            required
                            autoFocus
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setEmailError(null);
                            }}
                            onBlur={() => setEmail((value) => value.trim())}
                            surface="filled"
                            size="md"
                            state={emailError ? "error" : undefined}
                            aria-describedby={
                                emailError ? EMAIL_ERROR_ID : undefined
                            }
                            placeholder="you@example.com"
                            disabled={isSubmitting}
                            className={AUTH_INPUT_CLASS}
                        />
                        {emailError && (
                            <Text
                                id={EMAIL_ERROR_ID}
                                variant="fine-print"
                                role="alert"
                                className="text-error-base text-pretty"
                            >
                                {emailError}
                            </Text>
                        )}
                    </OrgAuthField>

                    {formError && (
                        <Text
                            id={FORM_ERROR_ID}
                            variant="small"
                            role="alert"
                            aria-live="polite"
                            className="text-error-base text-pretty"
                        >
                            {formError}
                        </Text>
                    )}
                </div>

                <Button
                    type="submit"
                    variant="raised"
                    interaction="lifted"
                    size="md"
                    fullWidth
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                            <Spinner surface="dark" className="size-3.5" />
                            Sending
                        </span>
                    ) : (
                        "Send reset link"
                    )}
                </Button>

                <div className="flex justify-center">
                    <Text
                        variant="small"
                        className="text-foreground-base underline underline-offset-2 hover:text-foreground-muted"
                        render={<Link to="/login" />}
                    >
                        Return to sign-in
                    </Text>
                </div>
            </form>
        </OrgAuthShell>
    );
}
